import { useId } from 'react'

const themes = {
    portal: { colors: ['#f9e4e8', '#bb5278', '#713750', '#f4ba74'], label: 'An open portal surrounded by learning resources' },
    express: { colors: ['#fff0d9', '#e79a5f', '#654b70', '#8ac6b1'], label: 'A winding path of hands-on learning challenges and career discoveries' },
    hues: { colors: ['#f8e9df', '#d57786', '#694a73', '#edbd79'], label: 'Color swatches surrounding a guessing game board' },
    lilypad: { colors: ['#e6f1e4', '#6eaa87', '#355c56', '#f0c0a2'], label: 'A flower blooming above a lily pad in a pond' },
    society: { colors: ['#e0eee5', '#529787', '#285a55', '#eab783'], label: 'A miniature neighborhood of connected civic buildings' },
    terra: { colors: ['#e5e8fa', '#777bc4', '#41477c', '#a6d5bd'], label: 'An isometric world with colorful buildings and trees' },
    website: { colors: ['#f9ead8', '#d48b54', '#885138', '#e8bd69'], label: 'Layered web pages with a sun and botanical shapes' },
    jobs: { colors: ['#dcecf4', '#5b9bbd', '#315873', '#f0b773'], label: 'A winding route connecting opportunities on a map' },
    logic: { colors: ['#e6eef8', '#7299be', '#3e607e', '#bed4e8'], label: 'Logical symbols connected in a branching proof' },
    portfolio: { colors: ['#f5dde7', '#c26994', '#773b5a', '#e8b96e'], label: 'A colorful browser canvas with code and a sparkle' },
    language: { colors: ['#e0efe9', '#65a294', '#34665e', '#e9b878'], label: 'Overlapping speech bubbles with playful letterforms' },
    notes: { colors: ['#f5edda', '#c3a05b', '#79623d', '#98b7a4'], label: 'Layered notebook pages with checkmarks and a pencil' },
    music: { colors: ['#e8e3f5', '#9580c2', '#564577', '#e5a2b6'], label: 'A vinyl record, sound waves, and a concert ticket' },
    focus: { colors: ['#e4edf5', '#7c9fc2', '#445f7d', '#eab798'], label: 'A study timer above an open book and small stars' },
}

const Spark = ({ x, y, size = 15, color }) => (
    <path d={`M ${x} ${y - size} Q ${x} ${y} ${x + size} ${y} Q ${x} ${y} ${x} ${y + size} Q ${x} ${y} ${x - size} ${y} Q ${x} ${y} ${x} ${y - size}`} fill={color} />
)

const Building = ({ x, y, height, color, ink }) => (
    <g transform={`translate(${x} ${y})`}>
        <rect width="66" height={height} rx="7" fill={color} />
        <path d={`M -7 0 L 33 -24 L 73 0 Z`} fill={ink} />
        {[14, 40].map((left) => (
            <g key={left} fill="#fffaf3" opacity=".85">
                <rect x={left} y="15" width="12" height="14" rx="2" />
                {height > 70 && <rect x={left} y="40" width="12" height="14" rx="2" />}
            </g>
        ))}
        <rect x="26" y={height - 24} width="15" height="24" rx="3" fill={ink} />
    </g>
)

const Scene = ({ kind, accent, ink, highlight }) => {
    switch (kind) {
        case 'portal':
            return (
                <>
                    <ellipse cx="300" cy="291" rx="142" ry="16" fill={ink} opacity=".09" />
                    <path d="M 211 281 V 154 A 89 89 0 0 1 389 154 V 281 Z" fill={accent} />
                    <path d="M 233 281 V 156 A 67 67 0 0 1 367 156 V 281 Z" fill="#fffaf4" />
                    <path d="M 252 280 V 161 A 48 48 0 0 1 348 161 V 280 Z" fill={highlight} opacity=".4" />
                    <path d="M 272 277 L 300 213 L 328 277" fill="none" stroke={accent} strokeWidth="2" opacity=".5" />
                    <g transform="translate(139 139) rotate(-14)">
                        <rect width="65" height="85" rx="8" fill={highlight} />
                        <path d="M 14 0 V 85 M 27 26 H 51 M 27 38 H 47" stroke={ink} strokeWidth="3" opacity=".6" />
                    </g>
                    <g transform="translate(397 176) rotate(12)">
                        <rect width="67" height="53" rx="9" fill="#fffaf4" />
                        <path d="M 12 18 H 54 M 12 29 H 43 M 12 40 H 34" stroke={accent} strokeWidth="4" strokeLinecap="round" />
                    </g>
                    <circle cx="410" cy="100" r="24" fill={highlight} />
                    <Spark x={410} y={100} size={13} color={ink} />
                    <Spark x={184} y={88} size={10} color={accent} />
                </>
            )
        case 'express':
            return (
                <>
                    <path d="M 150 242 C 203 281 247 254 266 201 S 342 117 448 151" fill="none" stroke="#fffaf4" strokeWidth="25" strokeLinecap="round" />
                    <path d="M 150 242 C 203 281 247 254 266 201 S 342 117 448 151" fill="none" stroke={accent} strokeWidth="4" strokeDasharray="3 12" strokeLinecap="round" />
                    <g transform="translate(160 194) rotate(-9)">
                        <rect width="77" height="75" rx="12" fill={highlight} />
                        <path d="M 13 20 Q 26 11 38 20 Q 50 11 64 20 V 57 Q 50 48 38 57 Q 26 48 13 57 Z M 38 20 V 57" fill="none" stroke={ink} strokeWidth="3" strokeLinejoin="round" />
                    </g>
                    <g transform="translate(261 111) rotate(7)">
                        <rect width="80" height="83" rx="12" fill="#fffaf4" />
                        <path d="M 16 27 H 64 M 16 42 H 54 M 16 57 H 46" stroke={accent} strokeWidth="5" strokeLinecap="round" />
                        <circle cx="62" cy="61" r="12" fill={highlight} />
                        <path d="M 57 61 L 61 65 L 68 56" fill="none" stroke={ink} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </g>
                    <g transform="translate(381 115) rotate(10)">
                        <rect y="10" width="76" height="62" rx="10" fill={ink} />
                        <path d="M 24 11 V 2 H 52 V 11" fill="none" stroke={ink} strokeWidth="6" />
                        <path d="M 0 37 Q 38 52 76 37" fill="none" stroke={highlight} strokeWidth="3" />
                        <rect x="33" y="39" width="10" height="9" rx="2" fill={highlight} />
                    </g>
                    <Spark x={152} y={112} size={17} color={accent} />
                    <Spark x={452} y={254} size={14} color={highlight} />
                </>
            )
        case 'hues':
            return (
                <>
                    <rect x="164" y="94" width="272" height="188" rx="16" fill="#fffaf4" transform="rotate(-5 300 188)" />
                    {[
                        [185, 113, '#e99c8a'], [240, 113, '#edbd79'], [295, 113, '#a9c99c'], [350, 113, '#8bb9c5'],
                        [185, 168, '#c991ad'], [240, 168, '#d57786'], [295, 168, '#6eaa87'], [350, 168, '#8d89b8'],
                    ].map(([x, y, color]) => (
                        <rect key={`${x}-${y}`} x={x} y={y} width="45" height="45" rx="7" fill={color} />
                    ))}
                    <path d="M 190 235 H 345 M 190 252 H 293" stroke={ink} strokeWidth="5" strokeLinecap="round" opacity=".4" />
                    <circle cx="388" cy="251" r="36" fill={accent} />
                    <path d="M 373 251 L 384 262 L 405 239" stroke="#fffaf4" strokeWidth="5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                    <Spark x={141} y={163} size={13} color={highlight} />
                    <Spark x={458} y={115} size={17} color={accent} />
                </>
            )
        case 'lilypad':
            return (
                <>
                    <ellipse cx="300" cy="250" rx="158" ry="50" fill={accent} opacity=".2" />
                    <path d="M 166 241 C 175 204 243 190 304 207 L 326 237 L 342 210 C 407 211 438 234 433 254 C 427 283 362 300 295 295 C 218 294 160 272 166 241 Z" fill={accent} />
                    <path d="M 304 207 Q 284 238 300 268" stroke={ink} strokeWidth="3" fill="none" opacity=".5" />
                    {[0, 60, 120, 180, 240, 300].map((angle) => (
                        <ellipse key={angle} cx="300" cy="154" rx="19" ry="42" fill={highlight} stroke="#fffaf4" strokeWidth="2" transform={`rotate(${angle} 300 185)`} />
                    ))}
                    <circle cx="300" cy="185" r="20" fill="#e8aa68" />
                    <path d="M 300 208 V 242" stroke={ink} strokeWidth="4" strokeLinecap="round" />
                    <path d="M 176 148 Q 190 132 204 148 M 397 145 Q 411 129 425 145" stroke={accent} strokeWidth="3" fill="none" strokeLinecap="round" />
                    <Spark x={441} y={91} size={12} color={highlight} />
                </>
            )
        case 'society':
            return (
                <>
                    <path d="M 128 266 Q 300 315 474 265" stroke={accent} strokeWidth="28" strokeLinecap="round" fill="none" opacity=".2" />
                    <path d="M 156 280 Q 300 235 441 281" stroke="#fffaf3" strokeWidth="3" strokeDasharray="8 10" fill="none" />
                    <Building x={160} y={171} height={81} color={highlight} ink={ink} />
                    <Building x={267} y={130} height={122} color={accent} ink={ink} />
                    <Building x={374} y={181} height={71} color={highlight} ink={ink} />
                    <path d="M 300 104 V 60 L 336 69 L 300 80" stroke={ink} strokeWidth="3" fill={highlight} />
                    <g fill={accent}>
                        <circle cx="129" cy="227" r="20" />
                        <circle cx="474" cy="231" r="18" />
                    </g>
                    <path d="M 129 240 V 265 M 474 245 V 267" stroke={ink} strokeWidth="5" strokeLinecap="round" />
                    <circle cx="420" cy="92" r="22" fill={highlight} opacity=".75" />
                    <path d="M 137 113 Q 163 94 185 113 M 387 132 Q 404 119 422 132" stroke={accent} strokeWidth="3" fill="none" strokeLinecap="round" />
                </>
            )
        case 'terra':
            return (
                <>
                    <path d="M 129 229 L 300 145 L 471 229 L 300 316 Z" fill={ink} opacity=".12" />
                    <path d="M 129 210 L 300 124 L 471 210 L 300 296 Z" fill={highlight} />
                    <path d="M 129 210 V 229 L 300 315 V 296 Z" fill={accent} />
                    <path d="M 300 296 L 471 210 V 229 L 300 315 Z" fill={ink} />
                    <path d="M 214 167 L 385 252 M 214 253 L 385 167" stroke="#fffaf3" strokeWidth="12" opacity=".65" />
                    {[[246, 112, 65], [335, 151, 45], [215, 203, 34]].map(([x, y, height]) => (
                        <g key={x}>
                            <path d={`M ${x} ${y} l 34 -17 l 34 17 l -34 17 Z`} fill="#fffaf3" />
                            <path d={`M ${x} ${y} v ${height} l 34 17 v -${height} Z`} fill={accent} />
                            <path d={`M ${x + 34} ${y + 17} l 34 -17 v ${height} l -34 17 Z`} fill={ink} />
                            <path d={`M ${x + 45} ${y + 26} l 10 -5 v 14 l -10 5 Z`} fill={highlight} />
                        </g>
                    ))}
                    <path d="M 398 212 V 171 M 175 207 V 178" stroke={ink} strokeWidth="5" />
                    <circle cx="398" cy="162" r="20" fill={accent} />
                    <circle cx="175" cy="167" r="16" fill={accent} />
                    <Spark x={422} y={99} color={highlight} />
                    <Spark x={174} y={91} size={9} color={accent} />
                </>
            )
        case 'website':
        case 'portfolio':
            return (
                <>
                    <rect x="161" y="84" width="283" height="181" rx="16" fill={accent} opacity=".2" transform="rotate(-8 302 175)" />
                    <rect x="148" y="92" width="292" height="188" rx="14" fill="#fffaf4" stroke={accent} strokeWidth="2" />
                    <path d="M 149 126 H 439" stroke={accent} strokeWidth="2" opacity=".4" />
                    {[166, 180, 194].map((cx) => <circle key={cx} cx={cx} cy="110" r="4" fill={accent} />)}
                    {kind === 'website' ? (
                        <>
                            <rect x="170" y="147" width="113" height="111" rx="7" fill={highlight} opacity=".5" />
                            <circle cx="226" cy="181" r="21" fill={accent} />
                            <path d="M 182 243 Q 212 193 226 232 Q 251 189 275 243 Z" fill={ink} opacity=".75" />
                            <path d="M 305 158 H 414 M 305 176 H 396" stroke={ink} strokeWidth="7" strokeLinecap="round" />
                            <path d="M 305 201 H 410 M 305 214 H 405" stroke={accent} strokeWidth="4" strokeLinecap="round" opacity=".6" />
                            <rect x="303" y="235" width="66" height="19" rx="9" fill={accent} />
                            <path d="M 462 252 Q 449 204 477 174 M 460 229 Q 436 231 434 207 Q 459 203 460 229 M 463 211 Q 486 212 492 188 Q 467 184 463 211" fill={ink} />
                        </>
                    ) : (
                        <>
                            <rect x="169" y="146" width="97" height="111" rx="8" fill={accent} opacity=".18" />
                            <circle cx="217" cy="179" r="20" fill={highlight} />
                            <path d="M 185 240 Q 181 205 217 205 Q 253 205 250 240 Z" fill={accent} />
                            <text x="292" y="185" fontFamily="monospace" fontSize="34" fontWeight="bold" fill={ink}>{'</>'}</text>
                            <path d="M 292 208 H 400 M 292 224 H 365 M 292 240 H 385" stroke={accent} strokeWidth="5" strokeLinecap="round" />
                            <circle cx="444" cy="238" r="34" fill={highlight} />
                            <Spark x={444} y={238} size={21} color={ink} />
                        </>
                    )}
                    <Spark x={132} y={83} size={13} color={accent} />
                    <Spark x={459} y={91} size={9} color={highlight} />
                </>
            )
        case 'jobs':
            return (
                <>
                    <g stroke={accent} strokeWidth="2" opacity=".2" fill="none">
                        <path d="M 136 88 L 178 284 M 218 77 L 246 286 M 328 78 L 309 288 M 428 78 L 394 285 M 467 135 L 133 179 M 461 233 L 144 247" />
                    </g>
                    <path d="M 169 246 C 141 178 270 249 274 184 S 426 216 422 118" stroke="#fffaf4" strokeWidth="16" strokeLinecap="round" fill="none" />
                    <path d="M 169 246 C 141 178 270 249 274 184 S 426 216 422 118" stroke={accent} strokeWidth="4" strokeDasharray="2 10" strokeLinecap="round" fill="none" />
                    {[[169, 245], [274, 184], [422, 118]].map(([x, y], i) => (
                        <g key={x} transform={`translate(${x} ${y})`}>
                            <path d="M 0 5 C -40 -32 -19 -62 0 -62 C 19 -62 40 -32 0 5 Z" fill={i === 1 ? highlight : accent} />
                            <circle cy="-36" r="10" fill="#fffaf4" />
                        </g>
                    ))}
                    <g transform="translate(351 240) rotate(-9)">
                        <rect width="89" height="55" rx="9" fill={ink} />
                        <path d="M 30 0 V -10 H 59 V 0" stroke={ink} strokeWidth="7" fill="none" />
                        <path d="M 0 23 Q 45 37 89 23" stroke={highlight} strokeWidth="2" fill="none" />
                        <rect x="40" y="24" width="10" height="10" rx="2" fill={highlight} />
                    </g>
                    <Spark x={166} y={107} size={13} color={highlight} />
                </>
            )
        case 'logic':
            return (
                <>
                    <path d="M 211 115 H 300 V 176 M 389 115 H 300 M 300 222 V 261 H 411" stroke={accent} strokeWidth="4" fill="none" strokeLinecap="round" />
                    <circle cx="211" cy="115" r="37" fill="#fffaf4" />
                    <circle cx="389" cy="115" r="37" fill="#fffaf4" />
                    <text x="211" y="129" textAnchor="middle" fill={ink} fontSize="39" fontFamily="Georgia, serif" fontStyle="italic">p</text>
                    <text x="389" y="129" textAnchor="middle" fill={ink} fontSize="39" fontFamily="Georgia, serif" fontStyle="italic">q</text>
                    <rect x="263" y="166" width="74" height="74" rx="15" fill={accent} transform="rotate(45 300 203)" />
                    <text x="300" y="217" textAnchor="middle" fill="#fffaf4" fontSize="42">∧</text>
                    <rect x="380" y="237" width="64" height="49" rx="13" fill={highlight} />
                    <path d="M 397 261 L 408 271 L 426 252" stroke={ink} strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                    <text x="165" y="258" fill={accent} fontSize="55" fontFamily="Georgia, serif" opacity=".5">∴</text>
                    <Spark x={451} y={178} size={11} color={accent} />
                </>
            )
        case 'language':
            return (
                <>
                    <g transform="rotate(-8 242 152)">
                        <path d="M 158 84 H 304 Q 323 84 323 103 V 181 Q 323 200 304 200 H 208 L 170 229 L 177 200 H 158 Q 139 200 139 181 V 103 Q 139 84 158 84 Z" fill={accent} />
                        <text x="229" y="165" textAnchor="middle" fill="#fffaf4" fontFamily="Georgia, serif" fontSize="65">Aa</text>
                    </g>
                    <g transform="rotate(8 365 230)">
                        <path d="M 293 162 H 439 Q 458 162 458 181 V 250 Q 458 269 439 269 H 424 L 431 298 L 393 269 H 293 Q 274 269 274 250 V 181 Q 274 162 293 162 Z" fill="#fffaf4" />
                        <text x="366" y="239" textAnchor="middle" fill={ink} fontFamily="Georgia, serif" fontSize="55">á é</text>
                    </g>
                    <path d="M 350 94 Q 390 84 404 122 M 404 122 L 405 104 M 404 122 L 386 118" stroke={ink} strokeWidth="3" strokeLinecap="round" fill="none" />
                    <Spark x={175} y={274} size={16} color={highlight} />
                    <circle cx="462" cy="104" r="13" fill={highlight} />
                </>
            )
        case 'notes':
            return (
                <>
                    <rect x="182" y="77" width="200" height="220" rx="12" fill={highlight} transform="rotate(-12 282 187)" />
                    <rect x="202" y="71" width="200" height="220" rx="12" fill="#fffaf4" transform="rotate(5 302 181)" />
                    <g transform="rotate(5 302 181)">
                        <path d="M 227 103 H 311" stroke={ink} strokeWidth="7" strokeLinecap="round" />
                        {[140, 181, 222].map((y, i) => (
                            <g key={y}>
                                <rect x="226" y={y} width="18" height="18" rx="4" stroke={accent} strokeWidth="2" fill={i === 2 ? 'none' : highlight} />
                                {i < 2 && <path d={`M 230 ${y + 8} l 4 4 l 7 -8`} stroke={ink} strokeWidth="2" fill="none" strokeLinecap="round" />}
                                <path d={`M 258 ${y + 9} H ${i === 1 ? 348 : 372}`} stroke={accent} strokeWidth="4" strokeLinecap="round" opacity=".55" />
                            </g>
                        ))}
                    </g>
                    <g transform="translate(442 118) rotate(23)">
                        <rect x="-9" width="18" height="133" rx="4" fill={accent} />
                        <path d="M -9 133 L 0 157 L 9 133 Z" fill={ink} />
                        <rect x="-9" y="9" width="18" height="10" fill={highlight} />
                    </g>
                    <Spark x={146} y={154} size={16} color={accent} />
                </>
            )
        case 'music':
            return (
                <>
                    <g stroke={accent} strokeWidth="7" strokeLinecap="round" opacity=".5">
                        {[25, 53, 85, 46, 103, 64, 33, 75, 110, 56, 30].map((height, i) => (
                            <path key={i} d={`M ${145 + i * 31} ${179 - height / 2} v ${height}`} />
                        ))}
                    </g>
                    <circle cx="278" cy="174" r="96" fill={ink} />
                    {[78, 67, 56].map((r) => <circle key={r} cx="278" cy="174" r={r} stroke="#fffaf4" strokeWidth="1" fill="none" opacity=".2" />)}
                    <circle cx="278" cy="174" r="34" fill={highlight} />
                    <circle cx="278" cy="174" r="7" fill={ink} />
                    <g transform="translate(340 221) rotate(-16)">
                        <path d="M 0 0 H 126 V 18 A 10 10 0 0 0 126 38 V 56 H 0 V 38 A 10 10 0 0 0 0 18 Z" fill="#fffaf4" />
                        <path d="M 91 6 V 50" stroke={accent} strokeWidth="2" strokeDasharray="4 4" />
                        <path d="M 18 18 H 72 M 18 29 H 61 M 18 40 H 67" stroke={accent} strokeWidth="3" strokeLinecap="round" />
                    </g>
                    <Spark x={427} y={92} size={18} color={highlight} />
                </>
            )
        case 'focus':
            return (
                <>
                    <circle cx="301" cy="150" r="77" fill="#fffaf4" />
                    <circle cx="301" cy="150" r="61" fill="none" stroke={accent} strokeWidth="8" opacity=".25" />
                    <path d="M 301 89 A 61 61 0 1 1 244 172" fill="none" stroke={accent} strokeWidth="8" strokeLinecap="round" />
                    <path d="M 301 111 V 150 L 328 169" fill="none" stroke={ink} strokeWidth="5" strokeLinecap="round" />
                    <rect x="286" y="52" width="30" height="13" rx="5" fill={ink} />
                    <path d="M 301 224 Q 237 195 169 215 V 275 Q 237 259 301 287 Q 365 259 433 275 V 215 Q 365 195 301 224 Z" fill={highlight} />
                    <path d="M 301 230 V 281 M 193 234 Q 239 225 278 240 M 193 252 Q 239 243 278 258 M 324 240 Q 363 225 409 234 M 324 258 Q 363 243 409 252" stroke={ink} strokeWidth="2" fill="none" opacity=".5" />
                    <Spark x={167} y={123} size={18} color={highlight} />
                    <Spark x={434} y={159} size={13} color={accent} />
                </>
            )
        default:
            return <Spark x={300} y={180} size={80} color={accent} />
    }
}

const ProjectArtwork = ({ project, className = '' }) => {
    const id = useId().replace(/:/g, '')

    if (project.thumbnail) {
        return <img src={project.thumbnail} alt={`${project.title} preview`} loading="lazy" className={className} />
    }

    const { colors, label } = themes[project.artwork] || themes.portfolio
    const [background, accent, ink, highlight] = colors

    return (
        <svg
            role="img"
            aria-labelledby={`${id}-title`}
            className={className}
        >
            <title id={`${id}-title`}>{`${project.title}: ${label}. Concept illustration.`}</title>
            <defs>
                <linearGradient id={`${id}-background`} x1="0" y1="0" x2="1" y2="1">
                    <stop stopColor={background} />
                    <stop offset="1" stopColor="#fffaf4" />
                </linearGradient>
                <pattern id={`${id}-dots`} width="22" height="22" patternUnits="userSpaceOnUse">
                    <circle cx="1" cy="1" r="1" fill={ink} opacity=".1" />
                </pattern>
            </defs>
            <rect width="100%" height="100%" fill={`url(#${id}-background)`} />
            <circle cx="95%" cy="5%" r="40%" fill={accent} opacity=".07" />
            <circle cx="5%" cy="95%" r="35%" fill={highlight} opacity=".13" />
            <rect width="100%" height="100%" fill={`url(#${id}-dots)`} />
            <svg width="100%" height="100%" viewBox="100 35 400 295" aria-hidden="true">
                <circle cx="300" cy="180" r="133" fill={background} opacity=".6" />
                <Scene kind={project.artwork} accent={accent} ink={ink} highlight={highlight} />
            </svg>
        </svg>
    )
}

export default ProjectArtwork
